import React, { useEffect, useState } from 'react';
import { bool, func, string, object } from 'prop-types';
import { connect } from 'react-redux';

import Modal from 'ps-components/Modal/Modal';
import ModalHeader from 'ps-components/Modal/ModalHeader/ModalHeader';
import ModalBody from 'ps-components/Modal/ModalBody/ModalBody';
import { FormGroup, Input } from 'ps-design';
import ReactSelect from 'ps-components/ReactSelect/ReactSelect';

import apiClient from 'ps-components/lib/apiClient';

import './JDXSettingsModal.scss';

JDXSettingsModal.propTypes = {
  show: bool,
  onClose: func,
  masterAccountId: string
};

function JDXSettingsModal({ show, onClose, masterAccountId }) {
  function closeModal() {
    onClose();
  }

  return (
    <Modal className="jdx-settings-modal" allowClose={true} show={show} onClose={closeModal}>
      <ModalHeader>
        <h2 className="pxl-header-sm">JDX Settings</h2>
      </ModalHeader>
      <ModalBody className="jdx-settings-modal__body">
        <JDXSettingsSection title="API Key">
          <APIKeySection />
        </JDXSettingsSection>
        <JDXSettingsSection title="Job Code UDF">
          <JobCodeUDFSection masterAccountId={masterAccountId} show={show} />
        </JDXSettingsSection>
      </ModalBody>
    </Modal>
  );
}
JDXSettingsSection.propTypes = {
  title: string,
  children: object
};

function JDXSettingsSection({ title, children }) {
  return (
    <div className="jdx-settings__section">
      <div className="jdx-settings__section-title">{title}</div>
      <div className="jdx-settings__section-content">{children}</div>
    </div>
  );
}

function APIKeySection() {
  const [apiKey, setApiKey] = useState(null);

  async function fetchApiKey() {
    // Fetch a new key
    const res = await apiClient.apiGet('/api/payscale/jdxpert/generateApiKey');
    if (res.data?.success) {
      setApiKey(res.data?.apiKey);
    }
  }
  return (
    <div className="api-key-modal__contents">
      <div className="api-key-modal__key">
        <div className="jdx-settings__description-text">
          To allow JDXpert to access your jobs in MarketPay, you must generate an API key to enter into JDXpert's
          Configuration Page.
        </div>
        <div className="jdx-settings__description-text">
          Warning: For security reasons, we will only show this value once when it is created. You can always
          re-generate a new key.
        </div>
        <FormGroup className="api-key-modal__container">
          <a onClick={fetchApiKey} className="pxl-btn pxl-btn--primary api-key-modal__generate-button">
            Generate
          </a>
          <Input className="api-key-modal__input-field" id="api-key-value" value={apiKey || ''} disabled={true} />
        </FormGroup>
      </div>
    </div>
  );
}

JobCodeUDFSection.propTypes = {
  masterAccountId: string,
  show: bool
};

function JobCodeUDFSection({ masterAccountId, show }) {
  const [UDFOptions, setUDFOptions] = useState([]);
  const [UDFSelection, setUDFSelection] = useState(null);
  const [foundUDF, setFoundUDF] = useState(null);

  useEffect(() => {
    if (!UDFOptions.length && show) {
      getUDFList();
    }
    if (!UDFSelection && show) {
      getSelectedUDF();
    }
  }, [show]);

  useEffect(() => {
    if (UDFOptions.length && show && foundUDF) setUDFSelection(UDFOptions.find(option => option.value === foundUDF));
  }, [UDFOptions, foundUDF]);

  async function getUDFList() {
    const res = await apiClient.apiGet(`/cms/api/accounts/${masterAccountId}/user-defined-fields/job`);
    if (res.data?.status === 200)
      setUDFOptions([
        { value: '', label: 'None' },
        ...res.data.data.userDefinedFields.map(field => ({ value: field.columnName, label: field.columnTitle }))
      ]);
  }

  async function setUDFField(selection) {
    const res = await apiClient.apiGet(`/api/util/metadata/updateMetadata`, {
      params: {
        key: 'jdxJobCodeUDF',
        value: selection.value
      }
    });
    if (res.status === 200) setUDFSelection(selection);
  }

  async function getSelectedUDF() {
    const res = await apiClient.apiGet(`/api/util/metadata/getMetadata`, {
      params: {
        key: 'jdxJobCodeUDF'
      }
    });
    if (res.status === 200) setFoundUDF(res.data.metadata.jdxJobCodeUDF);
  }

  return (
    <div className="jdx-udf-selection__container">
      <div className="jdx-settings__description-text">
        Specify a UDF to send in place of MarketPay job codes for JDXpert to use in their system for their version of
        job codes. Select "None" to default to job code.
      </div>
      <ReactSelect value={UDFSelection} options={UDFOptions} onChange={setUDFField} />
    </div>
  );
}

const mapStateToProps = ({ session }) => {
  return {
    masterAccountId: session.organization.masterAccountId
  };
};

export default connect(mapStateToProps)(JDXSettingsModal);
